import { logError } from "../../../shared/logging";
import {
  ChartStyleSettings,
  ConditionConfiguration,
  ConditionField,
  DimensionField,
  FieldConfiguration,
  PivotGeneralSettings,
  ReportConfiguration,
  ReportField,
  ReportType,
  SortConfiguration,
  SortField,
  TabularGeneralSettings,
  TileSettings,
} from "../../../shared/reporting/api/biClient.types";
import { chartRelatedReportTypes } from "../../../shared/reporting/utils/chartRelatedReport.types";
import { isTabularReportType, isTileReportType } from "../../../shared/reporting/utils/reportTypeHelper";
import { configurationToConditions } from "../../components/builder/utils/configuration/configurations";
import { NoDuplicateKeys } from "../../utilities/typesHelper";
import { selectCurrentReport } from "../selectors/currentReportSelectors";
import { selectConditions, selectReportType } from "../selectors/fieldsStateSelectors";
import {
  selectDimensions,
  selectDimensionsForCurrentReportType,
  selectMeasures,
  selectMetrics,
} from "../selectors/metaDataSelectors";
import { createTypedAsyncThunk } from "../utils/createTypedAsyncThunk";
import { setFieldStatesConfiguration } from "../utils/fieldsStateSliceHelper";
import {
  createChartAction,
  createCurrentReportAction,
  createFieldsStateAction,
  createPivotAction,
  createTabularAction,
  createTileAction,
} from "../utils/storeUtils";
import { chartFieldsStateThunks } from "./chartFieldsStateThunks";
import { pivotFieldsStateThunks } from "./pivotFieldsStateThunks";
import { tabularFieldsStateThunks } from "./tabularFieldsStateThunks";

const initializeFieldsState = createTypedAsyncThunk(
  "fieldsState/initializeFieldsState",
  (
    data: {
      configuration: ReportConfiguration;
      reportType: ReportType;
    },
    { dispatch, getState }
  ) => {
    try {
      const { configuration, reportType } = data;

      const state = getState();
      dispatch(createFieldsStateAction("setReportType", reportType));
      const dimensions = selectDimensions(state);
      const measures = selectMeasures(state);
      const metrics = selectMetrics(state);
      setFieldStatesConfiguration(dimensions, measures, metrics, configuration, reportType, dispatch);
      dispatch(createCurrentReportAction("refreshSession"));
    } catch (error) {
      logError(error, "[initializeFieldsState]");
    }
  }
);

const resetFieldsState = createTypedAsyncThunk(
  "fieldsState/resetFieldsState",
  (reportType: ReportType | undefined, { dispatch }) => {
    dispatch(createTabularAction("resetToDefaultAction"));

    dispatch(createPivotAction("resetToDefaultAction", undefined));

    dispatch(createChartAction("resetToDefaultAction", reportType));

    dispatch(createTileAction("resetToDefaultAction"));
  }
);

const handleReportTypeChange = createTypedAsyncThunk(
  "fieldsState/handleReportTypeChange",
  (reportType: ReportType, { dispatch, getState }) => {
    const state = getState();
    const currentReport = selectCurrentReport(state);
    const currentConditions = selectConditions(state);
    const dimensions = selectDimensionsForCurrentReportType(state);

    const conditions = currentConditions.map((c) => c.config) || (currentReport?.configuration.conditions ?? []);
    dispatch(resetFieldsState(reportType));
    dispatch(createFieldsStateAction("setReportType", reportType));

    const result = configurationToConditions(conditions, dimensions);
    dispatch(setConditions(result));

    dispatch(createCurrentReportAction("refreshSession"));
  }
);

const setConditions = createTypedAsyncThunk<void, ConditionField[]>(
  "fields/setConditions",
  (conditions, { dispatch, getState }) => {
    const reportType = selectReportType(getState());
    if (!reportType) {
      throw new Error("Report type not initialized");
    }
    if (reportType === ReportType.Pivot) {
      dispatch(createPivotAction("setConditions", conditions));
    } else if (reportType === ReportType.Tabular) {
      dispatch(createTabularAction("setConditions", conditions));
    } else if (chartRelatedReportTypes.includes(reportType)) {
      dispatch(createChartAction("setConditions", conditions));
    } else if (reportType === ReportType.Tile) {
      dispatch(createTileAction("setConditions", conditions));
    } else {
      throw new Error("Report type not initialized");
    }
  }
);

const addCondition = createTypedAsyncThunk<void, { field: ConditionField; index: number }>(
  "fields/addCondition",
  (payload, { dispatch, getState }) => {
    const reportType = selectReportType(getState());

    if (!reportType) {
      throw new Error("Report type not initialized");
    }

    if (reportType === ReportType.Pivot) {
      dispatch(createPivotAction("addCondition", payload));
    } else if (reportType === ReportType.Tabular) {
      dispatch(createTabularAction("addCondition", payload));
    } else if (chartRelatedReportTypes.includes(reportType)) {
      dispatch(createChartAction("addCondition", payload));
    } else if (reportType === ReportType.Tile) {
      dispatch(createTileAction("addCondition", payload));
    } else {
      throw new Error("Report type not initialized");
    }
  }
);

const removeCondition = createTypedAsyncThunk<void, ConditionField>(
  "fields/removeCondition",
  (field, { dispatch, getState }) => {
    const reportType = selectReportType(getState());
    if (!reportType) {
      throw new Error("Report type not initialized");
    }
    if (reportType === ReportType.Pivot) {
      dispatch(createPivotAction("removeCondition", field));
    } else if (reportType === ReportType.Tabular) {
      dispatch(createTabularAction("removeCondition", field));
    } else if (chartRelatedReportTypes.includes(reportType)) {
      dispatch(createChartAction("removeCondition", field));
    } else if (reportType === ReportType.Tile) {
      dispatch(createTileAction("removeCondition", field));
    } else {
      throw new Error("Report type not initialized");
    }
  }
);

const moveCondition = createTypedAsyncThunk<void, { field: ConditionField; newIndex: number }>(
  "fields/moveCondition",
  (payload, { dispatch, getState }) => {
    const reportType = selectReportType(getState());
    if (!reportType) {
      throw new Error("Report type not initialized");
    }
    if (reportType === ReportType.Pivot) {
      dispatch(createPivotAction("moveCondition", payload));
    } else if (reportType === ReportType.Tabular) {
      dispatch(createTabularAction("moveCondition", payload));
    } else if (chartRelatedReportTypes.includes(reportType)) {
      dispatch(createChartAction("moveCondition", payload));
    } else if (reportType === ReportType.Tile) {
      dispatch(createTileAction("moveCondition", payload));
    } else {
      throw new Error("Report type not initialized");
    }
  }
);

const updateCondition = createTypedAsyncThunk<void, { field: ConditionField; changes: Partial<ConditionField> }>(
  "fields/updateCondition",
  (payload, { dispatch, getState }) => {
    const reportType = selectReportType(getState());
    if (!reportType) {
      throw new Error("Report type not initialized");
    }
    if (reportType === ReportType.Pivot) {
      dispatch(createPivotAction("updateCondition", payload));
    } else if (reportType === ReportType.Tabular) {
      dispatch(createTabularAction("updateCondition", payload));
    } else if (chartRelatedReportTypes.includes(reportType)) {
      dispatch(createChartAction("updateCondition", payload));
    } else if (reportType === ReportType.Tile) {
      dispatch(createTileAction("updateCondition", payload));
    } else {
      throw new Error("Report type not initialized");
    }
  }
);

const updateConditionConfig = createTypedAsyncThunk<
  void,
  { field: ConditionField; changes: Partial<ConditionConfiguration> }
>("fields/updateConditionConfig", (payload, { dispatch, getState }) => {
  const reportType = selectReportType(getState());
  if (!reportType) {
    throw new Error("Report type not initialized");
  }
  if (reportType === ReportType.Pivot) {
    dispatch(createPivotAction("updateConditionConfig", payload));
  } else if (reportType === ReportType.Tabular) {
    dispatch(createTabularAction("updateConditionConfig", payload));
  } else if (chartRelatedReportTypes.includes(reportType)) {
    dispatch(createChartAction("updateConditionConfig", payload));
  } else if (reportType === ReportType.Tile) {
    dispatch(createTileAction("updateConditionConfig", payload));
  } else {
    throw new Error("Report type not initialized");
  }
});

const setSorting = createTypedAsyncThunk<void, { fields: SortField[]; groupMetaNames: string[] }>(
  "fields/setSorting",
  (payload, { dispatch, getState }) => {
    const reportType = selectReportType(getState());
    if (!reportType) {
      throw new Error("Report type not initialized");
    }
    if (reportType === ReportType.Pivot) {
      dispatch(createPivotAction("setSorting", payload));
    } else if (reportType === ReportType.Tabular) {
      dispatch(createTabularAction("setSorting", payload));
    } else if (chartRelatedReportTypes.includes(reportType)) {
      dispatch(createChartAction("setSorting", payload));
    } else {
      throw new Error("Report type not initialized");
    }
  }
);

const addSorting = createTypedAsyncThunk<void, DimensionField>("fields/addSorting", (field, { dispatch, getState }) => {
  const reportType = selectReportType(getState());
  if (!reportType) {
    throw new Error("Report type not initialized");
  }
  if (reportType === ReportType.Pivot) {
    dispatch(createPivotAction("addSorting", field));
  } else if (reportType === ReportType.Tabular) {
    dispatch(createTabularAction("addSorting", field));
  } else if (chartRelatedReportTypes.includes(reportType)) {
    dispatch(createChartAction("addSorting", field));
  } else {
    throw new Error("Report type not initialized");
  }
});

const removeSorting = createTypedAsyncThunk<void, SortField>(
  "fields/removeSorting",
  (field, { dispatch, getState }) => {
    const reportType = selectReportType(getState());
    if (!reportType) {
      throw new Error("Report type not initialized");
    }
    if (reportType === ReportType.Pivot) {
      dispatch(createPivotAction("removeSorting", field));
    } else if (reportType === ReportType.Tabular) {
      dispatch(createTabularAction("removeSorting", field));
    } else if (chartRelatedReportTypes.includes(reportType)) {
      dispatch(createChartAction("removeSorting", field));
    } else {
      throw new Error("Report type not initialized");
    }
  }
);

const moveSorting = createTypedAsyncThunk<void, { field: SortField; newIndex: number }>(
  "fields/moveSorting",
  (payload, { dispatch, getState }) => {
    const reportType = selectReportType(getState());
    if (!reportType) {
      throw new Error("Report type not initialized");
    }
    if (reportType === ReportType.Pivot) {
      dispatch(createPivotAction("moveSorting", payload));
    } else if (reportType === ReportType.Tabular) {
      dispatch(createTabularAction("moveSorting", payload));
    } else if (chartRelatedReportTypes.includes(reportType)) {
      dispatch(createChartAction("moveSorting", payload));
    } else {
      throw new Error("Report type not initialized");
    }
  }
);

const updateSorting = createTypedAsyncThunk<void, { field: SortField; changes: Partial<SortField> }>(
  "fields/updateSorting",
  (payload, { dispatch, getState }) => {
    const reportType = selectReportType(getState());
    if (!reportType) {
      throw new Error("Report type not initialized");
    }
    if (reportType === ReportType.Pivot) {
      dispatch(createPivotAction("updateSorting", payload));
    } else if (reportType === ReportType.Tabular) {
      dispatch(createTabularAction("updateSorting", payload));
    } else if (chartRelatedReportTypes.includes(reportType)) {
      dispatch(createChartAction("updateSorting", payload));
    } else {
      throw new Error("Report type not initialized");
    }
  }
);

const updateSortingConfig = createTypedAsyncThunk<void, { field: SortField; changes: Partial<SortConfiguration> }>(
  "fields/updateSortingConfig",
  (payload, { dispatch, getState }) => {
    const reportType = selectReportType(getState());
    if (!reportType) {
      throw new Error("Report type not initialized");
    }
    if (reportType === ReportType.Pivot) {
      dispatch(createPivotAction("updateSortingConfig", payload));
    } else if (reportType === ReportType.Tabular) {
      dispatch(createTabularAction("updateSortingConfig", payload));
    } else if (chartRelatedReportTypes.includes(reportType)) {
      dispatch(createChartAction("updateSortingConfig", payload));
    } else {
      throw new Error("Report type not initialized");
    }
  }
);

const setSettings = createTypedAsyncThunk<
  void,
  { settings: PivotGeneralSettings | TabularGeneralSettings | ChartStyleSettings | TileSettings }
>("fields/setSettings", (payload, { dispatch, getState }) => {
  const reportType = selectReportType(getState());
  if (!reportType) {
    throw new Error("Report type not initialized");
  }
  if (reportType === ReportType.Pivot) {
    dispatch(createPivotAction("setSettings", payload.settings as PivotGeneralSettings));
  } else if (reportType === ReportType.Tabular) {
    dispatch(createTabularAction("setSettings", payload.settings as TabularGeneralSettings));
  } else if (chartRelatedReportTypes.includes(reportType)) {
    dispatch(createChartAction("setSettings", payload.settings as Partial<ChartStyleSettings>));
  } else if (reportType === ReportType.Tile) {
    dispatch(createTileAction("setSettings", payload.settings as TileSettings));
  } else {
    throw new Error("Report type not initialized");
  }
});

const updateSettings = createTypedAsyncThunk<
  void,
  {
    settings:
      | Partial<PivotGeneralSettings>
      | Partial<TabularGeneralSettings>
      | Partial<ChartStyleSettings>
      | Partial<TileSettings>;
  }
>("fields/updateSettings", (payload, { dispatch, getState }) => {
  const reportType = selectReportType(getState());

  if (!reportType) {
    throw new Error("Report type not initialized");
  }
  if (reportType === ReportType.Pivot) {
    dispatch(createPivotAction("updateSettings", payload.settings as Partial<PivotGeneralSettings>));
  } else if (reportType === ReportType.Tabular) {
    dispatch(createTabularAction("updateSettings", payload.settings as Partial<TabularGeneralSettings>));
  } else if (chartRelatedReportTypes.includes(reportType)) {
    dispatch(createChartAction("updateSettings", payload.settings as Partial<ChartStyleSettings>));
  } else if (reportType === ReportType.Tile) {
    dispatch(createTileAction("updateSettings", payload.settings as Partial<TileSettings>));
  } else {
    throw new Error("Report type not initialized");
  }
});

const setMeasures = createTypedAsyncThunk<void, ReportField[]>(
  "fields/setMeasures",
  (measures, { dispatch, getState }) => {
    const reportType = selectReportType(getState());
    if (!reportType) {
      throw new Error("Report type not initialized");
    }
    if (reportType === ReportType.Pivot) {
      dispatch(createPivotAction("setMeasures", measures));
    } else if (chartRelatedReportTypes.includes(reportType)) {
      dispatch(createChartAction("setMeasures", measures));

      measures.forEach((field) => dispatch(createChartAction("addSeries", field.config.guid)));
    } else {
      throw new Error("Report type not initialized");
    }
  }
);

const addMeasure = createTypedAsyncThunk<void, { field: ReportField; index: number }>(
  "fields/addMeasure",
  (payload, { dispatch, getState }) => {
    const reportType = selectReportType(getState());
    if (!reportType) {
      throw new Error("Report type not initialized");
    }
    if (reportType === ReportType.Pivot) {
      dispatch(createPivotAction("addMeasure", payload));
    } else if (chartRelatedReportTypes.includes(reportType)) {
      dispatch(createChartAction("addMeasure", payload));

      dispatch(createChartAction("addSeries", payload.field.config.guid));
    } else {
      throw new Error("Report type not initialized");
    }
  }
);

const removeMeasure = createTypedAsyncThunk<void, ReportField>(
  "fields/removeMeasure",
  (field, { dispatch, getState }) => {
    const reportType = selectReportType(getState());
    if (!reportType) {
      throw new Error("Report type not initialized");
    }
    if (reportType === ReportType.Pivot) {
      dispatch(createPivotAction("removeMeasure", field));
    } else if (chartRelatedReportTypes.includes(reportType)) {
      dispatch(createChartAction("removeMeasure", field));

      dispatch(createChartAction("removeSeries", field.config.guid));
    } else {
      throw new Error("Report type not initialized");
    }
  }
);

const moveMeasure = createTypedAsyncThunk<void, { field: ReportField; newIndex: number }>(
  "fields/moveMeasure",
  (payload, { dispatch, getState }) => {
    const reportType = selectReportType(getState());
    if (!reportType) {
      throw new Error("Report type not initialized");
    }
    if (reportType === ReportType.Pivot) {
      dispatch(createPivotAction("moveMeasure", payload));
    } else if (chartRelatedReportTypes.includes(reportType)) {
      dispatch(createChartAction("moveMeasure", payload));
    } else {
      throw new Error("Report type not initialized");
    }
  }
);

const updateMeasure = createTypedAsyncThunk<void, { field: ReportField; changes: Partial<ReportField> }>(
  "fields/updateMeasure",
  (payload, { dispatch, getState }) => {
    const reportType = selectReportType(getState());
    if (!reportType) {
      throw new Error("Report type not initialized");
    }
    if (reportType === ReportType.Pivot) {
      dispatch(createPivotAction("updateMeasure", payload));
    } else if (chartRelatedReportTypes.includes(reportType)) {
      dispatch(createChartAction("updateMeasure", payload));
    } else {
      throw new Error("Report type not initialized");
    }
  }
);

const updateMeasureConfig = createTypedAsyncThunk<void, { field: ReportField; changes: Partial<FieldConfiguration> }>(
  "fields/updateMeasureConfig",
  (payload, { dispatch, getState }) => {
    const reportType = selectReportType(getState());
    if (!reportType) {
      throw new Error("Report type not initialized");
    }
    if (reportType === ReportType.Pivot) {
      dispatch(createPivotAction("updateMeasureConfig", payload));
    } else if (chartRelatedReportTypes.includes(reportType)) {
      dispatch(createChartAction("updateMeasureConfig", payload));
    } else {
      throw new Error("Report type not initialized");
    }
  }
);

const updateFieldConfig = createTypedAsyncThunk<void, { field: ReportField; changes: Partial<FieldConfiguration> }>(
  "fields/updateFieldConfig",
  (payload: { field: ReportField; changes: Partial<FieldConfiguration> }, { dispatch, getState }) => {
    const reportType = selectReportType(getState());
    if (isTileReportType(reportType)) {
      dispatch(createTileAction("updateFieldConfig", payload));
    } else if (isTabularReportType(reportType)) {
      dispatch(tabularFieldsStateThunks.updateTabularFieldConfig(payload));
    } else {
      throw new Error("Report type not initialized");
    }
  }
);

const updateMeasureField = createTypedAsyncThunk<void, { field: ReportField; changes: Partial<ReportField> }>(
  "fields/updateMeasureField",
  (payload, { dispatch, getState }) => {
    const reportType = selectReportType(getState());
    if (isTileReportType(reportType)) {
      dispatch(createTileAction("updateMeasureField", payload));
    } else if (isTabularReportType(reportType)) {
      dispatch(createTabularAction("updateMeasureField", payload));
    } else {
      throw new Error("Report type not initialized");
    }
  }
);

const updateFieldsOrder = createTypedAsyncThunk<void, string[]>(
  "fields/updateFieldsOrder",
  (payload, { dispatch, getState }) => {
    const reportType = selectReportType(getState());

    if (isTileReportType(reportType)) {
      dispatch(createTileAction("updateFieldsOrder", payload));
    } else if (isTabularReportType(reportType)) {
      dispatch(createTabularAction("updateFieldsOrder", payload));
    } else {
      throw new Error("Report type not initialized");
    }
  }
);

const moveField = createTypedAsyncThunk<void, { field: ReportField; newIndex: number }>(
  "fields/moveField",
  (payload, { dispatch, getState }) => {
    const reportType = selectReportType(getState());
    if (isTileReportType(reportType)) {
      dispatch(createTileAction("moveField", payload));
    } else if (isTabularReportType(reportType)) {
      dispatch(createTabularAction("moveField", payload));
    } else {
      throw new Error("Report type not initialized");
    }
  }
);

const removeField = createTypedAsyncThunk<void, ReportField>(
  "fields/removeField",
  (payload, { dispatch, getState }) => {
    const reportType = selectReportType(getState());
    if (isTileReportType(reportType)) {
      dispatch(createTileAction("removeField", payload));
    } else if (isTabularReportType(reportType)) {
      dispatch(createTabularAction("removeField", payload));
    } else {
      throw new Error("Report type not initialized");
    }
  }
);

const addField = createTypedAsyncThunk<void, { field: ReportField; index: number }>(
  "fields/addField",
  (payload, { dispatch, getState }) => {
    const reportType = selectReportType(getState());
    if (isTileReportType(reportType)) {
      dispatch(createTileAction("addField", payload));
    } else if (isTabularReportType(reportType)) {
      dispatch(createTabularAction("addField", payload));
    } else {
      throw new Error("Report type not initialized");
    }
  }
);

const setFields = createTypedAsyncThunk<void, ReportField[]>("fields/setFields", (payload, { dispatch, getState }) => {
  const reportType = selectReportType(getState());
  if (isTileReportType(reportType)) {
    dispatch(createTileAction("setFields", payload));
  } else if (isTabularReportType(reportType)) {
    dispatch(createTabularAction("setFields", payload));
  } else {
    throw new Error("Report type not initialized");
  }
});

const fieldStateThunks = {
  initializeFieldsState,
  handleReportTypeChange,
  setConditions,
  addCondition,
  removeCondition,
  moveCondition,
  updateCondition,
  updateConditionConfig,
  setSorting,
  addSorting,
  removeSorting,
  moveSorting,
  updateSorting,
  updateSortingConfig,
  setSettings,
  updateSettings,
  setMeasures,
  addMeasure,
  removeMeasure,
  moveMeasure,
  updateMeasure,
  updateMeasureConfig,
  updateFieldConfig,
  updateMeasureField,
  resetFieldsState,
  updateFieldsOrder,
  moveField,
  removeField,
  addField,
  setFields,
};

export const fieldsStateActions = {
  ...fieldStateThunks,
  // Type to ensure no duplicate keys between objects
  ...(tabularFieldsStateThunks as NoDuplicateKeys<typeof tabularFieldsStateThunks, typeof pivotFieldsStateThunks>),
  ...(pivotFieldsStateThunks as NoDuplicateKeys<typeof pivotFieldsStateThunks, typeof tabularFieldsStateThunks>),
  ...(chartFieldsStateThunks as NoDuplicateKeys<typeof chartFieldsStateThunks, typeof pivotFieldsStateThunks>),
};
